// @flow
import React, {useEffect, useRef, useState} from "react";
import Header from "./Header";
import BackButton from "../GUI_COMMON/COMPONENTS/BackButton";
import ArchiverClientApi from "../API/archiverClientApi";
import {Redirect, useParams} from "react-router-dom";
import LoadingSpinner from "../GUI_COMMON/COMPONENTS/LoadingSpinner";
import LIUtils from "../GUI_COMMON/SUPPORT/LIUtils";
import DatePicker from "react-date-picker";
import ArchiverGuiUtils from "../SUPPORT/ArchiverGuiUtils";
import Chart from "chart.js/auto"; // eslint-disable-line
import {Bar, Line} from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import {useCookies} from "react-cookie";
import TextInput from "../GUI_COMMON/COMPONENTS/TextInput";
import LIButton from "../GUI_COMMON/COMPONENTS/LIButton";
import CalendarIcon from "./CalendarIcon";
import ReloadButton from "../GUI_COMMON/COMPONENTS/ReloadButton";
import Popup from "../GUI_COMMON/COMPONENTS/Popup";

const RunReportPage = () => {

    const [isLoadingReport, setIsLoadingReport] = useState(false);
    const [isLoadingResult, setIsLoadingResult] = useState(false);
    const [reportResults, setReportResults ] = useState([]);
    const [filteredResults, setFilteredResults] = useState(null);
    const [reportBasicInfo, setReportBasicInfo] = useState();
    const [showAs, setShowAs] = useState("table");
    const [showGraphOption, setShowGraphOption] = useState(false);
    const [redirectTo, setRedirectTo] = useState();
    const {reportId} = useParams();

    const [cookies,setCookies]= useCookies();
    
    const [fromDate:Date, setFromDate] = useState(null); 
    const [toDate:Date, setToDate] = useState(null); 
    const [isDownloadOnly, setIsDownloadOnly] = useState(false);
    const [fetchHandle:Promise, setFetchHandle] = useState(null);

    const textFilterRef = useRef(null);
    
    let gCSVExportData = null;
    let gCSVExportHeader = null;

    useEffect(() => {
        return () => {
            if (fetchHandle?.cancel) fetchHandle.cancel();
        };
    }, [fetchHandle]);
    
    useEffect(() => {
        console.debug("RunReportPage starting up...");
        if(reportId) {
            setIsLoadingReport(true);
            ArchiverClientApi.getReportDetails(reportId)
            .then(({basicInfo}) => {
                setReportResults([]);
                setReportBasicInfo(basicInfo);
                if(hasGraphVerbs(basicInfo)) {
                    setShowGraphOption(true);
                    setShowAs("graph");
                }
                else {
                    setShowGraphOption(false);
                    setShowAs("table");
                }
                let runRightAway=true;
                setIsDownloadOnly(false);
                if(isReportDownloadOnly(basicInfo)) { // download only report - don't run immeidately
                    setIsDownloadOnly(true);
                    runRightAway=false;
                }
                if(hasDateColumnName(basicInfo)) {
                    runRightAway=false; // let the date ranges update the report data
                    const fromOffsetDays = getOffsetDaysFromCode(basicInfo.code); // was offset specified in the report?
                    if (fromOffsetDays>0) {
                        setFromDate(new Date(Date.now() - fromOffsetDays * 3600 * 24 * 1000));
                    }
                    else {
                        if (!fromDate) { // use 2 weeks as a default
                            setFromDate(new Date(Date.now() - 2 * 7 * 3600 * 24 * 1000)); //  2 weeks ago.
                        }
                    }
                }
                if(runRightAway) {
                    updateReportData(reportId); // no date ranges
                }
            })
            .catch( responseError => ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError))
            .finally(() => {
                setIsLoadingReport(false);
            });
        }
    }, [reportId]);  // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect( () => {
        if(fromDate || toDate ) {
            let fromTicks = fromDate===null?"":computeTimeTFromDateGivenUTC(fromDate.getFullYear(),fromDate.getMonth(),fromDate.getDate()); 
            let toTicks = toDate===null?"":computeTimeTFromDateGivenUTC(toDate.getFullYear(),toDate.getMonth(),toDate.getDate(),0,0,0,true); 
            
            if(Number.isNaN(fromTicks)) {
                fromTicks="";
            }
            if(Number.isNaN(toTicks)) {
                toTicks="";
            }
            if(!isReportDownloadOnly(reportBasicInfo)) { // update report data immediately
                updateReportData(reportId, fromTicks, toTicks);
            }

            setCookies('reportFromTicks', fromTicks===""?null:fromTicks, {path: '/'});
            setCookies('reportToTicks', toTicks===""?null:toTicks, {path: '/'});
        }
    }, [fromDate,toDate]); // eslint-disable-line react-hooks/exhaustive-deps
    
    function getFromTicksFromCookies() {
        const value = cookies.reportFromTicks;
        if(!value || value === "" || value === "null") return null;
        return Number(value);
    }
    
    function getToTicksFromCookies() {
        const value= cookies.reportToTicks;
        if(!value || value === "" || value==="null") return null;
        return Number(value);
    }
    
    function isReportDownloadOnly(basicInfo) {
        return basicInfo?.isDownloadOnly;
    }
    
    function computeTimeTFromDateGivenUTC(year, month, day, hour=0,minute=0,second=0, doEOD=false) {
        let ticks = Date.UTC(year,month,day,hour,minute,second)/1000;
        //localDT.setFullYear(year);
        //localDT.setMonth(month); // starts at 0
        //localDT.setDate(day); // starts at 1 with 0 being the last day of the previous month

        //let ticks = Math.floor(localDT.setHours(hour,minute,second)/1000);

        //let ticks = Math.floor(Date(year,month,day,hour,minute,second) / 1000);
        if(doEOD) {
            ticks += 86399; // include entire day of ticks
        }
        return ticks;
    }
    
    function hasDateColumnName(basicInfo) {
        return LIUtils.existsAndNotEmpty(basicInfo?.dateColumnName);
    }
    
    function getOffsetDaysFromCode(code) {
        const codeLines = code.split("\n");
        const offsetLine = codeLines.filter( l => l.startsWith("FROM_OFFSET_DAYS:"));
        if(offsetLine[0]) {
            return Number(offsetLine[0].substring("FROM_OFFSET_DAYS:".length).trim());
        }
        return 0;
    }
    
    function hasGraphVerbs(basicInfo) {
        if(!LIUtils.existsAndNotEmpty(basicInfo?.code)) {
            return false;
        }
        return basicInfo.code.indexOf("GRAPH_")!==-1;
    }
    
    function updateReportData(reportId, fromTicks=null, toTicks=null) {
        setIsLoadingResult(true);
        const ourTzOffset = -(new Date().getTimezoneOffset())/60;  // in hours
        console.debug("our tz offset is ",ourTzOffset);
        if(isDownloadOnly) {
            const handle = ArchiverClientApi.runReportAsCSVDownloadOnly(reportId,fromTicks, toTicks, ourTzOffset)
            setFetchHandle(handle); // for cancel
            handle.then(() => {
                Popup.showInfo("Report downloaded successfully", true, 0, true);
            })
            .catch( responseError => {
                ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError);
            })
            .finally( () => {
                setIsLoadingResult(false);
                setFetchHandle(null);
            });
        }
        else {
            ArchiverClientApi.runReport(reportId, fromTicks, toTicks, ourTzOffset)
            .then(({basicInfo, resultRows}) => {
                setReportBasicInfo(basicInfo);
                setReportResults(resultRows);
                handleDataFilterChanged();
            })
            .catch(responseError => {
                ArchiverGuiUtils.ShowErrorIfNotUnauthourized(responseError);
            })
            .finally(() => {
                setIsLoadingResult(false);
            });
        }
    }
     
    function handleDateChanged(name,value:Date) {
        if(name==="fromDate") {
            setFromDate(value);
        }
        else if(name==="toDate") {
            setToDate(value);
        }
    }
    
    function handleDataFilterChanged() {
        if(textFilterRef.current) {
            const filterText = textFilterRef.current.value;
            if(filterText==="") {
                setFilteredResults(null);
            }
            else {
                const filtered = reportResults.filter( row => {
                    for(const [,value] of Object.entries(row)) {
                        if(value["value"].toString().toLowerCase().indexOf(filterText.toLowerCase())!==-1) {
                            return true;
                        }
                    }
                    return false;
                });
                setFilteredResults(filtered);
            }
        }
    }
    
    function handleClearFilterClicked() {
        if(textFilterRef.current) {
            textFilterRef.current.value = "";
        }
        setFilteredResults(null);
    }
    
    function dumpBasicInfo() {
        if(isLoadingReport) {
            return <LoadingSpinner medium center/>;
        }
        
        if(!LIUtils.existsAndNotEmpty(reportBasicInfo)) {
            return null;
        }
        
        // a bit hacky, but we get newlines mapped this way.
        const descLines = reportBasicInfo?.description?.split('\n')??[];
        const desc = descLines.map( (item,index) => <div key={index}>{item}</div>);
        
        return(
            <div>
                <div className="row m-0 p-0 justify-content-between">
                    <h4>{reportBasicInfo?.name??"no name"}</h4>
                        <ReloadButton/>
                </div>
                <h4>
                </h4>
                <div className="text-wrap ml-5" style={{whiteSpace: 'pre-line'}}>
                    {desc??"no description"}
                </div>
            </div>
        );
    }
    
    function dumpResults() {
        if(isLoadingResult) {
            return <LoadingSpinner medium center/>;
        }
        
        if(!LIUtils.existsAndNotEmpty(reportResults) || reportResults.length===0) {
            return <div className="badge-pill mt-5 p-3 li-bg-card text-center">no results</div>;
        }
        if(showAs==="table") {
            return dumpResultsAsTable();
        }
        else if(showAs==="graph") {
            return dumpResultsAsGraph();
        }
        else {
            return dumpResultsAsCSV();
        }
    }
    
    function dateConvertIfNeeded(colName:string, colValue:string, dateFormat:string):string {
        if(looksLikeADate(colName, colValue)) { // convert?
            switch(dateFormat) {
                // ### TBD
                case "hour": return colValue;
                case "dayhour-local": {
                    const dt = new Date(LIUtils.dateFromJsonUtcDateStr(colValue));
                    if (dt instanceof Date && !isNaN(dt.valueOf())) {
                        return dt.toLocaleDateString("en-US", {
                            // no utc convert
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "numeric",
                            minute: "numeric",
                        }).replace(/,/g, '');
                    }
                    return colValue;
                }
                case "day": {
                    const dt = new Date(LIUtils.dateFromJsonUtcDateStr(colValue));
                    if (dt instanceof Date && !isNaN(dt.valueOf())) {
                        return dt.toLocaleDateString("en-US", {
                            timeZone: 'UTC', 
                            month: "short", 
                            day: "numeric", 
                            year: "numeric"
                        }).replace(/,/g, '');
                    }
                    return colValue;
                }
                
                case "week": {
                    const dt = new Date(LIUtils.dateFromJsonUtcDateStr(colValue));
                    if(dt instanceof Date && !isNaN(dt.valueOf())) {
                        return dt.toLocaleDateString("en-US", {
                            timeZone: 'UTC',
                            month: "short",
                            day: "numeric",
                            year: "numeric"
                        }).replace(/,/g,'');
                    }
                    return colValue;
                }
                case "month": {
                    const dt = new Date(LIUtils.dateFromJsonUtcDateStr(colValue));
                    if(dt instanceof Date && !isNaN(dt.valueOf())) {
                        return dt.toLocaleDateString("en-US", {
                            timeZone: 'UTC', 
                            month: "short", 
                            year: "numeric"
                        }).replace(/,/g,'');
                    }
                    return colValue;
                }
                case "year": 
                    const [year] = colValue.split('-');
                    return year;
                default: return colValue;
            }
        }
        return colValue; // no change
    }
    
    //#region MAYBE_USEFUL_LATER

    // function setDateFromCookie(key:string) {
    //     const value = cookies[key];
    //     if(!value || value === "") return null;
    //     return new Date(Number(value));
    // }

    // function shouldConvertDatesToLocal():boolean {
    //     return !reportBasicInfo["noUtcConvert"]??false;  // do it by default
    // }
    //
    // function looksLikeAStatDate(thisThing:string, theValue:string):boolean {
    //     if (theValue.endsWith(" 00:00:00")) { // ### HACK - likely a stat - don't convert
    //         return true;
    //     }
    //     return false;
    // }
    //
    // function dateToLocalIfDate(colName:string, colValue:string):string {
    //     if(looksLikeADate(colName, colValue)) { // convert to local time?
    //         if(shouldConvertDatesToLocal()) {
    //             return LIUtils.dateToShortDateTimeString(LIUtils.dateFromJsonUtcDateStr(colValue));
    //         }
    //         if(looksLikeAStatDate(colName, colValue)) { // a stat time frame (time is meaningless)
    //             const parts =colValue.split(' ',2);
    //             if(parts.length===2) { // have a date and time hopefully
    //                 return parts[0]; // return the date only
    //             } 
    //         }
    //     }
    //     return colValue; // no change
    // }
    //#endregion MAYBE_USEFUL_LATER
    
    function looksLikeADate(thisThing:string, theValue:string):boolean {
        const lowCase = thisThing.toLowerCase();
        const lowCaseParts = lowCase.split(' ');
        const dateNames = ["date","time_frame","time frame","timeframe","time","day","hour","week","month","year"];
        for(let index in dateNames) {
            if(lowCase===dateNames[index]) { // we have an exact match (this changed 250819 to be exact match -pott)
                return true;
            }
            // check each of the parts - slower
            for(let partIndex in lowCaseParts) {
                if(lowCaseParts[partIndex]===dateNames[index]) {
                    return true;
                }
            }
        }

        return false;
    }
    
    function looksLikeAnImage(thisThing:string):boolean {
        const lowCase = thisThing.toLowerCase();
        const lowCaseParts = lowCase.split(' ');
        const imageNames = ["image","pic","picture","snapshot","thumbnail"];
        for(let index in imageNames) {
            if(lowCase===imageNames[index]) { // we have an exact match (this changed 250819 to be exact match -pott)
                return true;
            }
            // check each of the parts - slower
            for(let partIndex in lowCaseParts) {
                if(lowCaseParts[partIndex]===imageNames[index]) {
                    return true;
                }
            }
        }

        return false;
        
    }
    
    function handleDownloadCSV(reportName) {
        // Validate presence of data/header
        if (!gCSVExportHeader || gCSVExportHeader.toString().trim() === "") {
            console.warn("CSV export aborted: missing header");
            return;
        }
        if (!gCSVExportData) {
            console.warn("CSV export aborted: no data available");
            return;
        }

        try {
            // Normalize data into an array of lines
            let lines = [];
            if (Array.isArray(gCSVExportData)) {
                // Remove any trailing newlines from each element; we will re-join consistently
                lines = gCSVExportData.map(l => (typeof l === 'string' ? l.replace(/\r?\n$/, '') : String(l)));
            } else if (typeof gCSVExportData === 'string') {
                lines = gCSVExportData.split(/\r?\n/);
                if (lines.length && lines[lines.length - 1] === '') lines.pop();
            } else {
                console.warn("CSV export aborted: unexpected data type", typeof gCSVExportData);
                return;
            }

            // Normalize header: single line, trim extraneous newlines
            let header = String(gCSVExportHeader).replace(/\r?\n/g, '').trim();
            if (header.length === 0) {
                console.warn("CSV export aborted: empty header after normalization");
                return;
            }

            // Build CSV text with CRLF and final newline; prepend BOM for UTF-8 Excel compatibility
            const csvText = [header, ...lines].join('\r\n') + '\r\n';
            const BOM = '\uFEFF';
            const blob = new Blob([BOM, csvText], { type: "text/csv;charset=utf-8;", endsWithNewline: true });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const now = new Date();
            const month = (now.getMonth() + 1).toString().padStart(2, '0');
            const day = now.getDate().toString().padStart(2, '0');
            const dateStamp = `${now.getFullYear()}${month}${day}`;
            link.download = `report-${reportName??""}-${dateStamp}.csv`; // ensure .csv extension
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("CSV export failed:", err);
        }
    }

    //#region RESULT_TYPES
    function dumpResultsAsTable() {

        const reportLines = reportBasicInfo.code.split("\n");
        const dateFormat = findGraphVarValue("DATETIME_DISPLAY:",reportLines)??"month";
        const imageSize = findGraphVarValue("IMAGE_SIZE:", reportLines)??null;
        let imageWidth=100; // suitable defaults?
        let imageHeight=100;
        if(imageSize) { // split out the width and height from the value
            [imageWidth,imageHeight] = imageSize.split("x"); // formatting is checked at the server
        }
        // rows have 1 or more subObjects which are the name/value pairs to report
        const resultsToShow = filteredResults??reportResults;
        if(resultsToShow.length===0) {
            return <div className="badge-pill mt-5 p-3 li-bg-card text-center">no results</div>;
        }
        const rowList = resultsToShow.map( (rowItem,index)=> {
            let retVal = [];
            for(const [key,value] of Object.entries(rowItem)) {
                let columnData = null;
                if(looksLikeAnImage(value["name"])) {
                    columnData = LIUtils.getImgFromImageData(value["value"],"",imageWidth,null,imageHeight,"image")
                }
                else {
                    columnData = dateConvertIfNeeded(value["name"], value["value"], dateFormat);
                }
                retVal.push(
                    <td key={key}>
                        {columnData}
                    </td>
                );
            }
            return <tr key={index}>{retVal}</tr>;
        });
        const headerText = getHeaderText(reportResults[0]);
        return(
            <table className="table table-sm w-100">
                {headerText}
                <tbody>
                    {rowList}
                </tbody>
            </table>
        );
    }

    function dumpResultsAsCSV() {
        
        const reportLines = reportBasicInfo.code.split("\n");
        const dateFormat = findGraphVarValue("DATETIME_DISPLAY:",reportLines)??"month";

        // rows have 1 or more subObjects which are the name/value pairs to report
        const resultsToShow = filteredResults??reportResults;
        const csvLinesAsText = resultsToShow.map( (rowItem,index)=> {
            let retVal = [];
            for(const [,value] of Object.entries(rowItem)) {
                let columnData = null;
                if(looksLikeAnImage(value["name"])) {
                    columnData = "(image data)";
                }
                else {
                    columnData = dateConvertIfNeeded(value["name"],value["value"], dateFormat);
                }
                
                // Basic safety fix: double any embedded quotes; final join will use CRLF for compatibility (Excel)
                let safeData = String(columnData).replace(/"/g, '""');
                safeData = quoteIfContainsComma(safeData);
                retVal.push(safeData);
            }
            return retVal.join(",")+"\n";
        });
        const headerText = getHeaderTextAsCSV(reportResults[0]);

        // set our globals
        gCSVExportData=csvLinesAsText;
        gCSVExportHeader=headerText;
        
        return(
            <div className="w-100">
                <div className="text-right">
                <LIButton onClick={() => handleDownloadCSV(reportBasicInfo.name)} label="Download CSV" faIcon="fa fa-download ml-1" primary small/>
                </div>
                <div className="font-weight-bold">{headerText}</div>
                <div style={{whiteSpace: 'pre-line'}}>{csvLinesAsText}</div>
            </div>
        );
    }
    
    function quoteIfContainsComma(str) {
        if(str.includes(",")) {
            return `"${str}"`;
        }
        return str;
    }

    function dumpResultsAsGraph() {

        const reportLines = reportBasicInfo.code.split("\n");
        // grab the values from the line-based report variable verbs
        const groupByVar = findGraphVarValue("GRAPH_GROUP_BY_OUTVAR:", reportLines);
        const graphYVarRaw = findGraphVarValue("GRAPH_Y_VAR:", reportLines);
        const graphYVars = graphYVarRaw ? graphYVarRaw.split(',').map(s => s.trim()).filter(s => s.length > 0) : [];
        const graphXVar = findGraphVarValue("GRAPH_X_VAR:", reportLines);
        const graphType = findGraphVarValue("GRAPH_TYPE:", reportLines)??"line";
        const dateFormat = findGraphVarValue("DATETIME_DISPLAY:",reportLines)??"month";

        const resultSets = {};
        let looksLikeTimeXAxis=false;

        const resultsToShow = filteredResults??reportResults;
        resultsToShow.map(
            rowItem => {
                // Build a map of the current row and extract X / group values
                const rowValues = {};
                let xValue = null;
                let groupValue = null;

                for(const [,dataSpecItem] of Object.entries(rowItem)) {
                    const name = dataSpecItem["name"];
                    const value = dataSpecItem["value"];
                    rowValues[name] = value;

                    if(groupByVar && name===groupByVar) {
                        groupValue = value;
                    }
                    else if(graphXVar && name===graphXVar) {
                        if(looksLikeADate(name,value)) {
                            looksLikeTimeXAxis=true;
                            // we don't need the utc conversion since the report is already specifying that.
                            xValue = LIUtils.dateFromJsonUtcDateStr(value, false); // value is a date -BUGFIX: 250820 - pott
                        }
                        else {
                            xValue = value; // value is just a value
                        }
                    }
                }

                // Determine which Y variables to plot for this row
                const yVarsToProcess = graphYVars.length > 0 ? graphYVars : [null];

                yVarsToProcess.forEach(yVarName => {
                    const yValue = yVarName ? rowValues[yVarName] : null;
                    if (graphYVars.length > 0 && yValue === undefined) {
                        return; // skip missing Y-column in this row
                    }

                    let resultSetName;
                    if (graphYVars.length === 0) {
                        // No Y var specified — legacy fallback
                        resultSetName = "Not Specified";
                    }
                    else if (graphYVars.length === 1) {
                        // Single Y var — preserve exact original naming behaviour
                        if (groupByVar && groupValue !== null) {
                            resultSetName = `${groupByVar}=${groupValue}`;
                        } else {
                            resultSetName = yVarName;
                        }
                    }
                    else {
                        // Multiple Y vars
                        if (groupByVar && groupValue !== null) {
                            resultSetName = `${yVarName} (${groupByVar}=${groupValue})`;
                        } else {
                            resultSetName = yVarName;
                        }
                    }

                    if(!resultSets[resultSetName.toString()]) {  // create
                        resultSets[resultSetName.toString()]=[];
                    }
                    resultSets[resultSetName.toString()].push([yValue,xValue]);
                });

                return null; // not used
            }
        );

        const data = {
            labels: [],
            datasets: []
        };
        const colors = [
            "#ff0000",
            "#0000ff",
            "#00ff00",
            "#dc6db3",
            "#00f6f6",
            "#f88844",
            "#f6f600",
            "#00f600",
            "#f600f6",
            "#aaaaaa",
            "#000000",
            "#777777",
            "#ff7777",
            "#7777ff",
            "#33ffaa",
            "#ff3300",
            "#ffaaaa"];

        let resultSetIndex=0;
        for(let [key,value] of Object.entries(resultSets)) {
            data.datasets.push({
                label: key,
                backgroundColor: colors[resultSetIndex % colors.length],
                borderColor: colors[resultSetIndex % colors.length],
                data: value.map(row => {
                    return {x: row[1], y: row[0]};
                }),
                tension: 0,
            });
            resultSetIndex++;
        }

        const xScaleTimeObj = {
            type: 'time',
            time: {
                displayFormats: {
                    // MM (1),MMM (Jan) ,MMMM (January)
                    // d (1), do (1st, 2nd), dd (01)
                    // E..EEE (Mon,), EEEE (Monday,), EEEEE (M,T,..), EEEEEE (Mo,Tu,We)
                    // H (0 - 23)), HH (00-23),
                    // mm, ss
                    // p (12:00 am)
                    // pp (12:00:00 am)
                    day: 'MMM dd',
                    hour: 'MMM dd HH:mm',
                },
                unit: dateFormat,
                tooltipFormat: getToolTipFormat(dateFormat),
            },
            stacked: (graphType==="stacked")
        }
        const xScaleLinearObj = {
            stacked: (graphType==="stacked")
        } // default

        const options =  {
            animation: true,
            parsing: {
                xAxisKey: 'x',
                yAxisKey: 'y'
            },
            scales: {
                x: looksLikeTimeXAxis?xScaleTimeObj:xScaleLinearObj,
                //offset: true,
                y: {
                    stacked: (graphType==="stacked")
                }
            }
        }

        return(
            <div>
                {graphType==='line' && <Line data={data} options={options}/>}
                {graphType==='bar' && <Bar data={data} options={options}/>}
                {graphType==='stacked' && <Bar data={data} options={options}/>}
            </div>
        );
    }
    //#endregion RESULT_TYPES

    function getHeaderTextAsCSV(sampleRow) {
        if(!sampleRow) {
            return null;
        }

        const headerRow = [];
        for(const [,value] of Object.entries(sampleRow)) {
            headerRow.push(value["name"]);
        }

        return headerRow.join(",");

    }
    
    function findGraphVarValue(graphVerb:string, reportLines:[]) {
        const varLine = reportLines.filter( l => l.startsWith(graphVerb));
        let varValue;
        if(varLine[0]) {
            varValue = varLine[0].substring(graphVerb.length).trim(); // this is our dataset selector
        }
        return varValue;
    }
    
    function getToolTipFormat(dateFormat) {
        switch(dateFormat) {
            case 'day': return 'MMM dd';
            case 'week': return 'MMM dd';
            case 'month': return 'MMM y';
            case 'year': return 'y';
            default: return 'MMM dd HH:mm';
        }
    }
    
    function dumpDateRangePicker() {
        if(!hasDateColumnName(reportBasicInfo)) { // no settable date ranges
            return null;
        }
        
        return(
            <div className="li-bg-blue-card row m-0 p-0">
                <div className="col">
                    <div className="li-form-label m-0">From</div>
                    <DatePicker className="form-control p-0" name="fromDate" clearIcon={null} calendarType="US"
                                onChange={(e)=>handleDateChanged("fromDate",e)} value={fromDate??null} calendarIcon={<CalendarIcon/>}
                                required disabled={false}/>
                    <small className="form-text text-muted">* month/day/year</small>
                </div>
                <div className="col">
                    <div className="li-form-label m-0">To</div>
                    <DatePicker className="form-control p-0" name="toDate" clearIcon={null} calendarType="US"
                                onChange={(e)=>handleDateChanged("toDate",e)} value={toDate??null} calendarIcon={<CalendarIcon/>}
                                required disabled={false}/>
                    <small className="form-text text-muted">* month/day/year</small>
                </div>
            </div>
        );
    }
    
    function dumpDataFilter() {
        return (
            <div className="col">
                <div className="row m-0 p-0 align-self-center justify-content-end">
                    <TextInput refName={textFilterRef} name="textFilter" label="Text Filter" oneRow textLeft 
                               maxLength={20} onChange={handleDataFilterChanged}/>
                    <div className="align-self-center">    
                        <LIButton bgClass="ml-1 mt-3" faIcon="fa fa-times-circle" small onClick={handleClearFilterClicked}/>
                    </div>
                </div>
            </div>
        );
    }
    
    function getHeaderText(sampleRow) {
        if(!sampleRow) {
            return null;
        }

        const headerRow = [];
        for(const [key,value] of Object.entries(sampleRow)) {
            headerRow.push(<th key={key}>{value["name"]}</th>);
        }
        
        return <thead className="li-bg-primary li-fg-white"><tr>{headerRow}</tr></thead>
        
    }

    function dumpReportTypeFilter() {
        return(
            <div className="mt-4 mr-3">
                <span className="li-fg-slightly-muted">Show As: </span>&nbsp;
                <span className={showAs==="table"?"font-weight-bold":"btn-link li-pointer"} onClick={()=>setShowAs("table")}>table</span> |&nbsp;
                <span className={showAs==="csv"?"font-weight-bold":"btn-link li-pointer"} onClick={()=>setShowAs("csv")}>csv</span>
                {showGraphOption && 
                    <span> |&nbsp;
                        <span className={showAs==="graph"?"font-weight-bold":"btn-link li-pointer"} onClick={()=>setShowAs("graph")}>graph</span>
                    </span>}
            </div>
        );
    }
    
    function dumpEditReportIfRequired() {
        if(LIUtils.UserHasLevel(cookies,"ADMIN")) {
            return <span className="li-border-dark rounded p-2 align-self-center li-pointer" 
                         onClick={() => setRedirectTo("/admin/reports/"+Number(reportId))}><i className="fas fa-cog mr-1"/>edit report</span>
        }
        return null;
    }
    
    function cancelReportRun() {

        if (fetchHandle?.cancel) {
            console.log("canceling report run");
            fetchHandle.cancel();
        }
        setIsLoadingResult(false);
        setFetchHandle(null);
    }
    
    function showDownloadButton() {
        return <div className="text-center mt-5">
            <LIButton label="Run and Download" primary faIcon="fa fa-download ml-2" 
                      onClick={() => updateReportData(reportId,getFromTicksFromCookies(),getToTicksFromCookies())} className="li-pointer"
                        disabled={isLoadingResult} showBusy={isLoadingResult}/>
            <LIButton label="Cancel" secondary onClick={cancelReportRun} bgClass="ml-4"
                      disabled={!isLoadingResult}/>
        </div>;
    }

    if(redirectTo) {
        return (
            <Redirect push to={redirectTo}/>
        );
    }
    
    return(
        <div className="container">
            <Header title={`Run Report - ${reportBasicInfo?.name}`}/>
            <div className="row li-header-row m-0 p-0 justify-content-between">
                    <BackButton/>
                    {dumpEditReportIfRequired()}
                    {dumpReportTypeFilter()}
            </div>
            {dumpBasicInfo()}
            
            <div className="p-2 my-2">
                {dumpDateRangePicker()}
                {!isDownloadOnly && dumpDataFilter()}
            </div>
            <hr/>

            {isDownloadOnly && showDownloadButton()}
            {!isDownloadOnly && dumpResults()}
        </div>
    );
}

export default RunReportPage;